import { defineContracts, endpoint } from '../featureModule'
import { identity } from './settings'

/**
 * Settings-owned route contracts (design 5.3.1): every settings endpoint a
 * migrated frontend consumes, path + params built once. The worker
 * registers its handlers through these objects
 * (worker/src/features/settings/api.ts) and the feature client
 * (frontend/src/features/settings/client.js) invokes the same objects.
 *
 * Cross-feature consumers (unchanged by the settings migration): the
 * calendar module's settings tab (CredentialSourceToggle needs the
 * 1Password connection list via listOpConnections), the duo/msgraph/
 * datadog/azure tabs (same list), and the datadog module's settings tab
 * (the Datadog Audit Event Stream card reads/writes the settings-owned
 * /api/audit/settings family).
 *
 * Same-path method pairs (GET + PUT /api/settings/general, GET + PUT
 * /api/settings/branding, PUT + DELETE /api/settings/branding/logo) are
 * contracted separately — contracts are method-aware.
 *
 * NOT contracted (no migrated frontend consumer — design 5.3.1 keeps them
 * behind the module's legacy prefixes): GET /api/gpg-keys/settings (the
 * Gpg tab reads rotation state from GET /api/gpg-keys), the umbrella
 * /api/integrations list/row lanes (legacy pages only), and the
 * /api/settings/target-catalog family (route not yet migrated — see the
 * settings api.ts header).
 */
export const contracts = defineContracts(identity, {
  // ---- Cross-feature surface (pre-migration adapter set) -----------------
  /** GET /api/op-connections — 1Password Connect server list. */
  listOpConnections: endpoint('GET', '/api/op-connections', { entitlement: 'settings' }),
  /** Cross-integration status summary (sidebar + integration pages). */
  integrationSummary: endpoint('GET', '/api/integration-summary', { entitlement: 'settings' }),
  /** GET /api/audit/settings — audit event stream configuration. */
  getAuditSettings: endpoint('GET', '/api/audit/settings', { entitlement: 'settings' }),
  /** PUT /api/audit/settings — update audit event stream configuration. */
  updateAuditSettings: endpoint('PUT', '/api/audit/settings', { entitlement: 'settings' }),
  /** POST /api/audit/settings/test-event — deliver a test audit event. */
  sendAuditTestEvent: endpoint('POST', '/api/audit/settings/test-event', { entitlement: 'settings' }),

  // ---- General (Settings → General tab) ----------------------------------
  /** GET /api/settings/general — workspace-level defaults (timezone). */
  getGeneralSettings: endpoint('GET', '/api/settings/general', { entitlement: 'settings' }),
  /** PUT /api/settings/general — update workspace-level defaults. */
  updateGeneralSettings: endpoint('PUT', '/api/settings/general', { entitlement: 'settings' }),

  // ---- Branding (Settings → Branding tab) --------------------------------
  /** GET /api/settings/branding — branding config + hasCustomLogo. */
  getBranding: endpoint('GET', '/api/settings/branding', { entitlement: 'settings' }),
  /** PUT /api/settings/branding — update name/subtitle/accent/from-name. */
  updateBranding: endpoint('PUT', '/api/settings/branding', { entitlement: 'settings' }),
  /** PUT /api/settings/branding/logo — upload the PNG logo bytes. */
  uploadBrandingLogo: endpoint('PUT', '/api/settings/branding/logo', { entitlement: 'settings' }),
  /** DELETE /api/settings/branding/logo — remove the custom logo. */
  deleteBrandingLogo: endpoint('DELETE', '/api/settings/branding/logo', { entitlement: 'settings' }),

  // ---- GPG keys (Settings → GPG Keys tab) ---------------------------------
  /** GET /api/gpg-keys — active key info + compatibility + rotation state. */
  getGpgKey: endpoint('GET', '/api/gpg-keys', { entitlement: 'settings' }),
  /** POST /api/gpg-keys/generate — first-time keypair generation. */
  generateGpgKey: endpoint('POST', '/api/gpg-keys/generate', { entitlement: 'settings' }),
  /** POST /api/gpg-keys/rotate — stage and distribute a signed transition. */
  rotateGpgKey: endpoint('POST', '/api/gpg-keys/rotate', { entitlement: 'settings' }),
  /** POST /api/gpg-keys/activate — cut over only after every required proof. */
  activateGpgRotation: endpoint('POST', '/api/gpg-keys/activate', { entitlement: 'settings' }),
  /** POST /api/gpg-keys/cancel — cancel a pre-activation transition. */
  cancelGpgRotation: endpoint('POST', '/api/gpg-keys/cancel', { entitlement: 'settings' }),
  /** POST /api/gpg-keys/exclude-agent — audited pre-activation fleet exclusion. */
  excludeGpgRotationAgent: endpoint('POST', '/api/gpg-keys/exclude-agent', { entitlement: 'settings' }),
  /** POST /api/gpg-keys/rollback — restore the old key during retirement grace. */
  rollbackGpgRotation: endpoint('POST', '/api/gpg-keys/rollback', { entitlement: 'settings' }),
  /** POST /api/gpg-keys/test — sign/verify + compatibility probe. */
  testGpgKey: endpoint('POST', '/api/gpg-keys/test', { entitlement: 'settings' }),
  /** POST /api/gpg-keys/repush-rotation — retry the current lifecycle phase. */
  repushGpgRotation: endpoint('POST', '/api/gpg-keys/repush-rotation', { entitlement: 'settings' }),
  /** PUT /api/gpg-keys/settings — auto-rotation toggle + interval. */
  updateGpgRotationSettings: endpoint('PUT', '/api/gpg-keys/settings', { entitlement: 'settings' }),

  // ---- 1Password connections (Settings → 1Password tab) -------------------
  /** POST /api/op-connections — create + auto-sync ingress. */
  createOpConnection: endpoint('POST', '/api/op-connections', { entitlement: 'settings' }),
  /** PUT /api/op-connections/:id — update + auto-sync on transport change. */
  updateOpConnection: endpoint('PUT', '/api/op-connections/:id', { entitlement: 'settings' }),
  /** DELETE /api/op-connections/:id — delete + ingress cleanup. */
  deleteOpConnection: endpoint('DELETE', '/api/op-connections/:id', { entitlement: 'settings' }),
  /** POST /api/op-connections/:id/test — health + token probe. */
  testOpConnection: endpoint('POST', '/api/op-connections/:id/test', { entitlement: 'settings' }),
  /** POST /api/op-connections/:id/resync — reconcile tunnel ingress. */
  resyncOpConnection: endpoint('POST', '/api/op-connections/:id/resync', { entitlement: 'settings' }),
  /** PUT /api/op-connections/:id/set-default — make default connection. */
  setDefaultOpConnection: endpoint('PUT', '/api/op-connections/:id/set-default', { entitlement: 'settings' }),

  // ---- Target catalogue (Settings → Target Catalog tab) -------------------
  // All served by targetCatalog.ts on the 'settings' gate — EXACT-path
  // contracts under the '/api/settings/target-catalog' prefix (they outrank
  // it by specificity; the gate is byte-identical).
  /** GET /api/settings/target-catalog/types — catalogue target-type facets. */
  listTargetCatalogTypes: endpoint('GET', '/api/settings/target-catalog/types', { entitlement: 'settings' }),
  /** GET /api/settings/target-catalog/integrations — per-integration sync state. */
  listTargetCatalogIntegrations: endpoint('GET', '/api/settings/target-catalog/integrations', { entitlement: 'settings' }),
  /** GET /api/settings/target-catalog/search?q&type&integration&… — catalogue search. */
  searchTargetCatalog: endpoint('GET', '/api/settings/target-catalog/search', { entitlement: 'settings' }),
  /** GET /api/settings/target-catalog/targets/:id — one catalogue row + aliases. */
  getTargetCatalogTarget: endpoint('GET', '/api/settings/target-catalog/targets/:id', { entitlement: 'settings' }),
  /** POST /api/settings/target-catalog/rebuild — rebuild from every source, server-ordered. Queued into the run-owner DO; answers 202 with the run status. */
  rebuildTargetCatalog: endpoint('POST', '/api/settings/target-catalog/rebuild', { entitlement: 'settings' }),
  /** GET /api/settings/target-catalog/rebuild/status — the queued rebuild's run status (UI polls until it settles). */
  rebuildTargetCatalogStatus: endpoint('GET', '/api/settings/target-catalog/rebuild/status', { entitlement: 'settings' }),
  /** GET /api/settings/target-catalog/attach-reports — last attach-sync outcome per attach-only integration (unmatched/ambiguous object names). */
  listTargetCatalogAttachReports: endpoint('GET', '/api/settings/target-catalog/attach-reports', { entitlement: 'settings' }),
  /** GET /api/settings/target-catalog/checkmk-join-suggestions — CheckMK hosts whose name matches exactly one agent, joinable in one click. */
  listCheckMKJoinSuggestions: endpoint('GET', '/api/settings/target-catalog/checkmk-join-suggestions', { entitlement: 'settings' }),
  /** POST /api/settings/target-catalog/checkmk-join-suggestions/apply — write the operations/agent_id label into CheckMK for every suggestion and re-attach. Queued into the run-owner DO; answers 202 with the run status. */
  applyCheckMKJoinSuggestions: endpoint('POST', '/api/settings/target-catalog/checkmk-join-suggestions/apply', { entitlement: 'settings' }),
  /** GET /api/settings/target-catalog/checkmk-join-suggestions/status — the queued join run's status (UI polls until it settles). */
  checkMKJoinApplyStatus: endpoint('GET', '/api/settings/target-catalog/checkmk-join-suggestions/status', { entitlement: 'settings' }),
  listWazuhJoinSuggestions: endpoint('GET', '/api/settings/target-catalog/wazuh-join-suggestions', { entitlement: 'settings' }),
  applyWazuhJoinSuggestions: endpoint('POST', '/api/settings/target-catalog/wazuh-join-suggestions/apply', { entitlement: 'settings' }),
  wazuhJoinApplyStatus: endpoint('GET', '/api/settings/target-catalog/wazuh-join-suggestions/status', { entitlement: 'settings' }),
  listVelociraptorJoinSuggestions: endpoint('GET', '/api/settings/target-catalog/velociraptor-join-suggestions', { entitlement: 'settings' }),
  applyVelociraptorJoinSuggestions: endpoint('POST', '/api/settings/target-catalog/velociraptor-join-suggestions/apply', { entitlement: 'settings' }),
  velociraptorJoinApplyStatus: endpoint('GET', '/api/settings/target-catalog/velociraptor-join-suggestions/status', { entitlement: 'settings' }),
  /** POST /api/settings/target-catalog/targets/:id/aliases — add a manual alias. */
  addTargetCatalogAlias: endpoint('POST', '/api/settings/target-catalog/targets/:id/aliases', { entitlement: 'settings' }),
  /** DELETE /api/settings/target-catalog/targets/:id/aliases/:alias — remove a manual alias. */
  deleteTargetCatalogAlias: endpoint('DELETE', '/api/settings/target-catalog/targets/:id/aliases/:alias', { entitlement: 'settings' }),

  // ---- Feedback relay (the global FeedbackModal) --------------------------
  /** POST /api/feedback — user feedback relay (forwarded to the provider). */
  submitFeedback: endpoint('POST', '/api/feedback', { entitlement: 'settings' }),
})
